<template>
  <div ref="mainRef" class="subscribe-payment-wrapper">
    <div class="subscribe__bg">
      <h1 class="subscribe__bg--title">
        {{ $t('you-yan-aigc-3d-shi-pin-chuang-zuo-ping-tai') }}
      </h1>
      <PlanSwitcher />
      <div
        class="flex justify-center relative mt-[40px]"
        :class="isGlobalVersion() ? 'gap-x-[16px]' : 'gap-x-[36px]'"
      >
        <RightsItem
          v-for="rights in displayRights"
          :key="rights.id + state.cycle"
          class="rights-wrapper"
          :class="`rights-bg__${rights.rights_type} ${
            !isChinese() && rights.rights_type !== USER_LEVEL_KEY.TASTE ? 'rights-en' : ''
          }`"
          :rights="rights"
          :compareRights="compareRights"
          :account-list="accountList"
        />
      </div>
    </div>
    <div class="subscribe__content">
      <div class="w-full pb-[80px]">
        <div v-if="!isChinese()" class="text-center h-[180px] -mt-[60px]">
          <Resource
            :src="getFeUrl('/youyan-platform/png/pay-group.png')"
            class="w-[621px]"
            :transformResource="transformImage"
          />
        </div>
        <div class="subscribe__content-title text-font-1 pt-[80px] pb-[32px]">
          {{ $t('hui-yuan-tao-can-quan-yi-dui-bi') }}
        </div>
        <RightsSame
          v-if="!pending"
          id="compare-rights"
          :key="userInfo?.user_id"
          :accountList="accountList"
          :rightsList="compareRightsHeader"
          :compareRights="compareRights"
          :colorsMap="colorsMap"
          :topHeight="offsetTopHeight"
          @get="getOffsetTopHeight"
        ></RightsSame>
        <!-- 企业 logo -->
        <div class="subscribe__content-title text-font-1 pt-[80px] pb-[15px]">
          <div class="mb-[38px]">
            {{ $t('zhong-duo-qi-ye-de-yi-zhi-xuan-ze') }}
          </div>
          <Resource
            class="w-[1200px] mx-auto block mb-[20px]"
            :src="getFeUrl('/youyan-platform/png/subscribe/logo-wall-3.png')"
            :resourceOption="{
              format: 'webp',
            }"
          />
          <Resource
            class="w-[1200px] mx-auto block"
            :src="getFeUrl('/youyan-platform/png/subscribe/logo-wall-4.png')"
            :resourceOption="{
              format: 'webp',
            }"
          />
        </div>
        <div class="subscribe__content-title text-font-1 pt-[80px] pb-[32px]">
          {{ $t('ge-ren-he-qi-ye-shang-yong-shou-quan-qu-bie') }}
        </div>
        <authorization-differences-content class="justify-center" />
        <!-- 常见问题 -->
        <div class="subscribe__content-title text-font-1 pt-[80px] pb-[32px]">
          {{ $t('chang-jian-wen-ti') }}
        </div>
        <FaqList v-if="rightsRes?.data?.faq?.length" :faq-list="rightsRes?.data?.faq || []" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { usePayment } from '@rendora/common/features/payment/store';
import { omit } from 'lodash-es';
import { BUY_TYPE } from '~/api/interface';
import { SHOW_ORG_KEY, USER_LEVEL_KEY } from '~/api/interface/auth';
import { getUserAccountList } from '~/api/module/auth';
import { reqRightsDisplay } from '~/api/module/rights';
import { actionUpdateCycle } from '~/composables/rights';
import AuthorizationDifferencesContent from '~/features/paymentDesk/components/buy-rights/authorization-differences-content.vue';
import { subscribeContact } from '~/hooks/useModal/modals/contact';
import { transformImage } from '~/utils/';
import { isChinese, isGlobalVersion } from '~/utils/area';
import { getFeUrl } from '~/utils/url';
import FaqList from './components/faq/index.vue';
import PlanSwitcher from './components/plan-switcher.vue';
import RightsSame from './components/rights-same/index.vue';
import RightsItem from './components/rights.vue';

const state = useRightsConfig();
const mainRef = ref();
const offsetTopHeight = ref(59);
const router = useRouter();
const paymentStore = usePayment();

const userInfo = useUserInfo();
const {
  data: rightsRes,
  refresh: rightsRefresh,
  status: rightsRefreshStatus,
} = useAsyncData(() => reqRightsDisplay({ faq: true }));

const pending = computed(
  () => rightsRefreshStatus.value === 'pending' || accountRefreshStatus.value === 'pending',
);

const {
  data: accountListRes,
  refresh: accountRefresh,
  status: accountRefreshStatus,
} = useAsyncData(() => {
  if (userInfo.value) {
    return getUserAccountList();
  }

  return Promise.resolve({
    code: 0,
    data: [],
  });
});

function removeKey16(data: any) {
  return data.map((item: any) => {
    if (item.items) {
      item.items = removeKey16(item.items);
    }
    item.rights_content = omit(item.rights_content, [USER_LEVEL_KEY.TEAM_LIGHT]);
    if (item.children) {
      item.children = item.children.map((v: any) => ({
        ...v,
        rights_content: omit(v.rights_content, [USER_LEVEL_KEY.TEAM_LIGHT]),
      }));
    }
    return item;
  });
}
const compareRights: any = computed(() => {
  const sortedRights = [...(rightsRes.value?.data?.rights_compare || [])];
  removeKey16(sortedRights);
  return sortedRights.sort((a, b) => a.idx - b.idx);
});

const colorsMap = {
  0: 'var(--back-bg)',
  [USER_LEVEL_KEY.TASTE]: 'var(--subscribe-table-taste)',
  [USER_LEVEL_KEY.PERSONAL_LITE]: 'var(--subscribe-table-lite)',
  [USER_LEVEL_KEY.PERSONAL]: 'var(--subscribe-table-personal)',
  [USER_LEVEL_KEY.TEAM_LIGHT]: 'var(--subscribe-table-light)',
  [SHOW_ORG_KEY.ORG_SHOW]: 'var( --subscribe-table-org)',
};

// 权益对比头部信息
const compareRightsHeader: any = computed(() => {
  return rightsRes.value?.data?.rights.filter(r => {
    const isRightsTypeOk = [
      USER_LEVEL_KEY.TASTE,
      USER_LEVEL_KEY.PERSONAL_LITE,
      USER_LEVEL_KEY.PERSONAL,
      USER_LEVEL_KEY.TEAM_LIGHT,
      SHOW_ORG_KEY.ORG_SHOW,
    ].includes(r.rights_type);

    return isRightsTypeOk;
  });
});

const displayRights = computed(() => {
  return rightsRes.value?.data?.rights.filter(r => {
    const isOrgShow = SHOW_ORG_KEY.ORG_SHOW === r.rights_type;

    const isRightsTypeOk = [
      USER_LEVEL_KEY.TASTE,
      USER_LEVEL_KEY.PERSONAL,
      USER_LEVEL_KEY.PERSONAL_LITE,
      // USER_LEVEL_KEY.TEAM_LIGHT,
      SHOW_ORG_KEY.ORG_SHOW,
    ].includes(r.rights_type);

    const isPerson = [USER_LEVEL_KEY.TASTE, USER_LEVEL_KEY.PERSONAL, USER_LEVEL_KEY.PERSONAL_LITE];
    const isCompany = [
      // USER_LEVEL_KEY.TEAM_LIGHT,
      SHOW_ORG_KEY.ORG_SHOW,
    ];

    if (state.value.cycle === 'person') {
      return isPerson.includes(r.rights_type);
    }

    if (state.value.cycle === 'team') {
      return isCompany.includes(r.rights_type);
    }

    const isPaymentCycleCorrect = r.rights_by_time.some(item => {
      if (!state.value.cycle || item.days === null) {
        return true;
      }

      if (state.value.cycle === 'annual') {
        return item.days === 365 && item.buy_type === BUY_TYPE.SUBSCRIBE;
      }

      if (state.value.cycle === 'monthly') {
        return item.days === 30 && item.buy_type === BUY_TYPE.SUBSCRIBE;
      }

      return false;
    });

    return isRightsTypeOk && (isOrgShow || isPaymentCycleCorrect);
  });
});

const accountList = computed(() => accountListRes.value?.data || []);

watch(
  () => userInfo.value?.user_id,
  _userInfo => {
    rightsRefresh();
    accountRefresh();
  },
);

const isPerson = [USER_LEVEL_KEY.TASTE, USER_LEVEL_KEY.PERSONAL, USER_LEVEL_KEY.PERSONAL_LITE];

if (userInfo.value?.user_id && !isPerson.includes(Number(userInfo.value.rights_type))) {
  if (!isGlobalVersion()) {
    actionUpdateCycle('team');
  }
}

const route = useRoute();

// 获取权益对比需要吸顶的距离值
const getOffsetTopHeight = () => {
  offsetTopHeight.value = mainRef.value?.offsetTop || 0;
};
// const state = useRightsConfig()
onMounted(async () => {
  await paymentStore.fetchDiscount();
  if (route.query.contact) {
    state.value.cycle = 'team';
    subscribeContact();
    // showContact('yygw-ybbzs-lz');
    router.replace({ query: {} });
  }
  nextTick(() => {
    if (import.meta.server) {
      return;
    }
    // 用事件委托的方式来实现
    document.addEventListener('click', e => {
      const el = e.target as HTMLAnchorElement;
      if (el?.classList.contains('contactUs')) {
        subscribeContact();
      }
    });
  });
});

const bannerBg = computed(() => `url(${getFeUrl('/youyan-platform/png/banner-partners@2x.png')})`);
const rights1Bg = computed(
  () => `url(${getFeUrl('/youyan-platform/png/subscribe/rights-bg-1.png')})`,
);
const rights3Bg = computed(
  () => `url(${getFeUrl('/youyan-platform/png/subscribe/rights-bg-2.png')})`,
);
const rights10Bg = computed(
  () => `url(${getFeUrl('/youyan-platform/png/subscribe/right-bg-3-1.png')})`,
);
const rights16Bg = computed(
  () => `url(${getFeUrl('/youyan-platform/png/subscribe/right-bg-4-2.png')})`,
);
const rights24Bg = computed(
  () => `url(${getFeUrl('/youyan-platform/png/subscribe/rights-bg-5-1.png')})`,
);
</script>

<style lang="scss">
.subscribe-payment-wrapper {
  --subscribe-rights-1: #fff;
  --subscribe-rights-3: #fff;
  --subscribe-rights-10: #fff;
  --subscribe-rights-16: #fff;
  --subscribe-rights-24: #fff;

  --subscribe-rights-1-top: linear-gradient(338deg, #b1edff 0%, #eafff7 100%);
  --subscribe-rights-3-top: linear-gradient(338deg, #b1edff 0%, #eafff7 100%);
  --subscribe-rights-10-top: linear-gradient(316deg, #d369f3 0%, #5a56fd 55%, #4553ff 100%);
  --subscribe-rights-16-top: linear-gradient(311deg, #60b3ef 0%, #2ce78a 100%);
  --subscribe-rights-24-top: linear-gradient(315deg, #3c0988 0%, #aa1ae2 100%);

  background: linear-gradient(353deg, #ffffff 0%, #f6ffff 37%, #e8eaf2 81%, #f1f2f7 100%);
  .subscribe__bg {
    padding: 80px 0;
    background: var(--subscribe-bg);

    .subscribe__bg--title {
      // margin-top: 80px;
      margin: 0;
      margin-bottom: 44px;
      font-size: 40px;
      line-height: 60px;
      text-align: center;
      font-weight: 600;
      color: var(--purple-10);
    }
    .subscribe__bg--banner {
      background-image: v-bind('bannerBg');
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
    }
    .sub-btn {
      width: 280px;
      height: 44px;
      border-radius: 8px;
      line-height: 44px;
      text-align: center;
      font-weight: var(--x-weight-medium, 500);
      cursor: pointer;
      &:hover {
        opacity: 0.8;
      }
    }
    .desc-title {
      font-size: 14px;
      line-height: 20px;
      margin: 32px 0 20px 0;
      color: var(--text-1);
      font-weight: var(--x-weight-medium, 500);
    }
    .sub-title {
      font-size: 12px;
      line-height: 20px;
      font-weight: var(--x-weight-medium, 500);
    }
    .title-name {
      font-size: 18px;
      line-height: 24px;
      font-weight: bold;
      margin-bottom: 10px;
    }
    .corner-mark {
      padding: 5px 11px;
      background-color: rgba(0, 0, 0, 0.5);
      border-radius: 10px 0 0 0;
      color: var(--warning-3);
      font-size: 12px;
      font-weight: var(--x-weight-medium, 500);
      line-height: 18px;
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      bottom: 0;
      right: 0;
    }
    .desc-line {
      display: flex;
      align-items: center;
      font-size: 14px;
      line-height: 20px;
      color: var(--text-1-70);
      margin-bottom: 16px;
    }
    .price-suffix {
      font-size: 14px;
      line-height: 20px;
      font-weight: var(--x-weight-medium, 500);
    }
  }
  .subscribe__content {
    background: var(--back-bg);
    .price-num {
      font-size: 40px;
      font-weight: 600;
    }
    .price-prefix {
      font-size: 40px;
      font-weight: 600;
    }
    .price-suffix {
      font-size: 20px;
    }

    .team-tags-bg {
      background: linear-gradient(307deg, #ffb87e 0%, #ff8929 100%);
      color: white;
    }
    .desc-title {
      // @apply text-font-1;
      color: #121926;
    }
    .desc-line {
      // @apply text-font-1;
      color: #121926;
    }
    .discount-price {
      // @apply text-font-1;
      color: #121926;
    }
    .base-price {
      // @apply text-font-80;
      color: rgba(18, 25, 38, 0.8);
      text-decoration: line-through;
    }
  }

  .subscribe__content-title {
    font-size: 24px;
    font-weight: bold;
    line-height: 36px;
    text-align: center;
  }

  .rights-bg {
    &__1 {
      max-width: 340px;
      box-shadow: 0px 2px 20px 0px rgba(106, 104, 130, 0.1);
      background: var(--subscribe-rights-1);
      .sub-title,
      .title-name {
        color: #1b4272;
      }
      .intro-bg {
        background-image: v-bind('rights1Bg');
        padding-top: 35px;
        padding-left: 32px;
        color: #ffffff;
        position: relative;
        height: 124px;
      }
      .sub-btn {
        background: var(--back-3);
        color: var(--text-1);
      }
      .sub-btn-disabled {
        background: var(--back-2);
        color: var(--text-1-30);
        cursor: auto;
        &:hover {
          opacity: 1;
        }
      }
      &:hover {
        transform: translateY(-18px);
      }
      .suit-text {
        opacity: 0.8;
      }
      .rights-info-subtitle {
      }
      .circle-bg {
        background: #adadad; // color: #726bff;
      }
      .circle-close-bg {
        background: #ffdddd;
        color: #ff5757;
      }
      .yy-btn-gray {
        background: #ffffff;
        box-shadow: 0px 2px 20px 0px rgba(105, 98, 255, 0.1) !important;
        border-radius: 10px;
        border: 1px solid rgb(242, 243, 243);
        &:hover {
          background: var(--purple-6) !important;
          color: #ffffff !important;
          box-shadow: 0px 2px 20px 0px rgba(105, 98, 255, 0.1);
          border-radius: 10px;
          border: 1px solid rgb(246, 248, 249);
        }
        &:disabled {
          background: #ffffff !important;
          box-shadow: 0px 2px 20px 0px rgba(105, 98, 255, 0.1);
          border-radius: 10px;
          color: var(--purple-6) !important;
          border: 1px solid rgb(242, 243, 243);
          opacity: 0.7;
        }
      }
    }
    &__3 {
      max-width: 340px;
      box-shadow: 0px 2px 20px 0px rgba(106, 104, 130, 0.1);
      background: var(--subscribe-rights-3);
      .intro-bg {
        background-image: v-bind('rights3Bg');
        padding-top: 35px;
        padding-left: 32px;
        color: #ffffff;
        position: relative;
        height: 124px;
        background-size: cover;
      }
      .price-num {
        font-size: 28px;
        line-height: 36px;
        font-weight: var(--x-weight-medium, 500);
      }
      .price-prefix {
        font-size: 28px;
        line-height: 36px;
        font-weight: var(--x-weight-medium, 500);
      }
      .sub-btn {
        background: linear-gradient(293deg, #fff3c9 0%, #f6db6f 100%);
        color: var(--warning-10);
      }

      &:hover {
        transform: translateY(-18px);
      }
      .suit-text {
        color: #205080;
      }
      .rights-info-subtitle {
        // color: rgba(255, 255, 255, 0.9);
      }
      .circle-bg {
        background: #adadad; // color: white;
      }
      .circle-close-bg {
        background: #fff;
        color: #ff5757;
      }
      .yy-btn-gray {
        color: var(--purple-6) !important;
        background: #ffffff;
        // box-shadow: none;
        &:hover {
          color: rgba(105, 98, 255, 0.7) !important;
        }
      }
    }
    &__10 {
      max-width: 340px;
      box-shadow: 0px 2px 20px 0px rgba(106, 104, 130, 0.1);
      background: var(--subscribe-rights-10);
      .intro-bg {
        background-image: v-bind('rights10Bg');
        padding-top: 35px;
        padding-left: 32px;
        color: #ffffff;
        position: relative;
        height: 124px;
        background-size: cover;
      }
      .price-num {
        font-size: 28px;
        line-height: 36px;
        font-weight: var(--x-weight-medium, 500);
      }
      .price-prefix {
        font-size: 28px;
        line-height: 36px;
        font-weight: var(--x-weight-medium, 500);
      }
      .sub-btn {
        background: linear-gradient(293deg, #fff3c9 0%, #f6db6f 100%);
        color: var(--warning-10);
      }

      &:hover {
        transform: translateY(-18px);
      }
      .suit-text {
        color: #205080;
      }
      .rights-info-subtitle {
        // color: rgba(255, 255, 255, 0.9);
      }
      .circle-bg {
        background: #adadad; // color: white;
      }
      .circle-close-bg {
        background: #fff;
        color: #ff5757;
      }
      .yy-btn-gray {
        color: var(--purple-6) !important;
        background: #ffffff;
        // box-shadow: none;
        &:hover {
          color: rgba(105, 98, 255, 0.7) !important;
        }
      }
    }
    &__15 {
      max-width: 340px;
      color: #063660;
      background: linear-gradient(338deg, #c1deff 0%, #f5fcfc 100%);
      .suit-text {
        color: #204080;
      }
    }
    &__16 {
      max-width: 380px;
      color: #063660;
      box-shadow: 0px 6px 30px 0px rgba(106, 104, 130, 0.1);
      background: var(--subscribe-rights-16);
      .intro-bg {
        background-image: v-bind('rights16Bg');
        color: #ffffff;
        position: relative;
        height: 124px;
        background-size: cover;
        padding-top: 35px;
        padding-left: 32px;
        color: #ffffff;
        position: relative;
      }
      .price-prefix,
      .price-num {
        font-size: 28px;
        line-height: 36px;
        font-weight: var(--x-weight-medium, 500);
      }
      .base-price {
        font-size: 14px;
        line-height: 26px;
        color: #868ca1;
        text-decoration: line-through;
      }
      .sub-btn {
        width: 320px;
        background: linear-gradient(293deg, #fff3c9 0%, #f6db6f 100%);
        color: var(--warning-10);
      }
      &:hover {
        transform: translateY(-18px);
      }
      .suit-text {
        color: #204080;
      }

      .circle-bg {
        background: #adadad; // color: white;
      }
      .circle-close-bg {
        background: #fff;
        color: #ff5757;
      }
      .yy-btn-gray {
        color: var(--purple-6) !important;
        background: #ffffff;
        // box-shadow: none;
        &:hover {
          color: rgba(105, 98, 255, 0.7) !important;
        }
      }
    }
    &__24 {
      max-width: 380px;
      color: var(--text-1);
      box-shadow: 0px 6px 30px 0px rgba(106, 104, 130, 0.1);
      background: var(--subscribe-rights-24);
      .shadow-bg {
        display: none;
      }
      .intro-bg {
        background-image: v-bind('rights24Bg');
        color: var(--warning-3);
        position: relative;
        height: 124px;
        background-size: cover;
        padding-top: 35px;
        padding-left: 32px;
      }
      .sub-btn {
        width: 320px;
        background: #271d0b;
        color: var(--warning-3);
      }
      &:hover {
        transform: translateY(-18px);
      }

      .suit-text {
        color: #3f3aa3;
      }
      .circle-bg {
        background: #adadad; // color: white;
      }
      .circle-close-bg {
        background: #fff;
        color: var(--error-6);
      }
      .yy-btn-gray {
        color: var(--purple-6) !important;
        background: #ffffff;
        // box-shadow: none;
        &:hover {
          color: rgba(105, 98, 255, 0.7) !important;
        }
      }
    }
  }
  .rights-extra {
    @apply absolute text-[14px] font-bold p-[7px_11px] bg-[#FFAC15] rounded-[10px_10px_10px_0] min-w-[108px]  -top-[38px] right-[0px] border-solid border border-white text-white;
    width: fit-content;
  }
  .rights-head {
    position: relative;
    top: 0;
    transition: all 0.5s ease-in-out;
    img {
      margin: 0 auto;
      margin-bottom: 10px;
      display: block;
      width: 40px;
      height: 40px;
    }
    &__hover {
      top: -47px;
      img {
        margin-bottom: 22px;
        width: 60px;
        height: 60px;
      }
    }
  }
  .rights-wrapper {
    flex: 1;
    padding-bottom: 40px;
    border-radius: 20px;
    // background: var(--back-0);
    // background: #ffffff;
    transition: all 0.4s ease-in-out;
    overflow: hidden;
    img {
      transition: all 0.4s ease-in-out;
    }
    &:hover {
      transition: all 0.3s ease-in-out;
      transform: translateY(-18px);
    }
  }
  .rights-en {
    .discount-price {
      color: #ffffff;
    }
    .circle-bg {
      background: rgba(233, 236, 255, 1);
    }
    .base-price {
      color: rgba(255, 255, 255, 0.8);
      text-decoration: line-through;
    }

    .desc-title {
      @apply text-white;
    }
    .desc-line {
      @apply text-white;
    }
  }
}
</style>
